/**
 * Algo Engine backtest replay API (Phase 1, admin-only; async execution
 * added 2026-09-19 "Fix reliability"; config-version selection removed
 * 2026-09-19 "Simplify Algo Backtest design" — a backtest always runs
 * against the algo's currently active config, resolved server-side).
 * `run()` returns almost immediately with `status: "QUEUED"` — it does NOT
 * wait for the replay to finish. The caller is expected to poll `getRun()`
 * to watch `status` progress QUEUED -> RUNNING -> COMPLETED/FAILED, reading
 * `progress_current`/`progress_total` for a live progress indicator while
 * RUNNING. This is what eliminates the 504 a long (e.g. multi-month,
 * 1-minute-resolution) backtest used to cause when it ran synchronously
 * inside the request. Mirrors `backend/app/api/v1/algo_backtest.py`
 * route-for-route.
 */

import api from "@/services/api";
import type { AlgoBacktestCreateRequest, AlgoBacktestDiagnostics, AlgoBacktestRun, AlgoBacktestTrade } from "@/types";

export const algoBacktestService = {
  /** Queues a new backtest run and returns immediately (HTTP 202) with
   * `status: "QUEUED"` — poll `getRun()` for progress/completion. */
  async run(algoId: string, payload: AlgoBacktestCreateRequest): Promise<AlgoBacktestRun> {
    const { data } = await api.post<AlgoBacktestRun>(`/algo/${algoId}/backtest`, payload);
    return data;
  },

  async listForAlgo(algoId: string): Promise<AlgoBacktestRun[]> {
    const { data } = await api.get<AlgoBacktestRun[]>(`/algo/${algoId}/backtest`);
    return data;
  },

  async getRun(algoId: string, runId: string): Promise<AlgoBacktestRun> {
    const { data } = await api.get<AlgoBacktestRun>(`/algo/${algoId}/backtest/${runId}`);
    return data;
  },

  async listTrades(algoId: string, runId: string): Promise<AlgoBacktestTrade[]> {
    const { data } = await api.get<AlgoBacktestTrade[]>(`/algo/${algoId}/backtest/${runId}/trades`);
    return data;
  },

  /** Phase 3 "Strategy Diagnostics" (2026-09-19) — deliberately NOT called
   * on every status poll: the caller fetches this only once, lazily, when
   * the UI's "Signal Diagnostics" subsection is actually expanded (see
   * `BacktestTab` in `AlgoManagementPage.tsx`), since it walks every
   * signal-log row for the run. */
  async getDiagnostics(algoId: string, runId: string): Promise<AlgoBacktestDiagnostics> {
    const { data } = await api.get<AlgoBacktestDiagnostics>(`/algo/${algoId}/backtest/${runId}/diagnostics`);
    return data;
  },
};

export default algoBacktestService;
